import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useFormikContext } from 'formik';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useUnlayerDraftContext } from '@/context/UnlayerDraftContext';
import { useUnlayerComposeInteractionsContext } from '@/context/UnlayerComposeInteractionsContext';

/**
 * Loads and selects a preset draft when the compose drawer opens with a dialog-provided
 * `draftId` (e.g. a draft Folio AI just created, opened from its toast or notification).
 */
export const usePresetDraftLoader = () => {
	const dialogData = useSelector((state) => state.dialog?.data?.unlayerEmail) ?? {};
	const email = useSelector((state) => state.msalAccount.account?.username);
	const { values } = useFormikContext();

	// Opened from a notification, the client and deal come from the page's object context and
	// can load after the drawer opens (the previous page's rows may show first). Formik
	// reinitialises when they do, which would wipe a draft loaded before then.
	const hasDraftScope =
		String(values.client?.clientId) === String(dialogData.draftClientId) &&
		(!dialogData.draftDealId || String(values.deal?.dealId) === String(dialogData.draftDealId));

	// Loading a draft checks access with the user's id and broker groups
	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });

	const { handleDraftSelectorOpen, setDraftActionError } = useUnlayerDraftContext();
	const { handleDraftSelect } = useUnlayerComposeInteractionsContext();

	// The drawer stays open when another toast or notification opens it with a different draft
	const [loadedPresetDraftId, setLoadedPresetDraftId] = useState('');

	useEffect(() => {
		if (
			!dialogData.draftId ||
			!userDetails ||
			!hasDraftScope ||
			loadedPresetDraftId === dialogData.draftId
		) {
			return;
		}
		setLoadedPresetDraftId(dialogData.draftId);
		// Loads the draft list so the selector can show the preset draft's label
		handleDraftSelectorOpen();
		handleDraftSelect(dialogData.draftId).catch(() =>
			setDraftActionError('Unable to load the Folio AI draft.'),
		);
	}, [
		dialogData.draftId,
		handleDraftSelect,
		handleDraftSelectorOpen,
		hasDraftScope,
		loadedPresetDraftId,
		setDraftActionError,
		userDetails,
	]);
};
